<script setup lang="ts">
import api from '@/api';
import { updateToken } from '@/api/backend/_http';
import type { UtmMetricDto } from '@/api/backend/users/applications';
import { AppValues, CurrentKey, LoanType } from '@/helpers/const';
import { initDeclarationMachine } from '@/helpers/loanDeclarationAppMachine';
import { initDischargeRequestMachine } from '@/helpers/loanDischargeRequestMachine';
import { initRequestIncomeExpenseMachine } from '@/helpers/loanRequestIncomeExpenseMachine';
import { useApplicationApi } from '@/helpers/useApplicationApi';
import { useScenarioApi } from '@/helpers/useScenarioApi';
import { useDischargeRequestApi } from '@/helpers/useDischargeRequestApi';
import { useBrokerLoanAppProcess } from '@/helpers/useBrokerLoanAppProcess';
import { useErhlLoanAppProcess } from '@/helpers/useErhlLoanAppProcess';
import { useDeclarationProcess } from '@/helpers/useDeclarationProcess';
import { useDischargeRequestProcess } from '@/helpers/useDischargeRequestProcess';
import { useLoanAppProcess } from '@/helpers/useLoanAppProcess';
import { useRequestIncomeExpenseProcess } from '@/helpers/useRequestIncomeExpenseProcess';
import { useFeatureFlags } from '@/helpers/useFeatureFlags';
import { resolveBrokerIdentity } from '@/helpers/brokerIdentity';
import { hasLiveBrokerSession } from '@/helpers/brokerSession';
import {
  DeclarationProcessStep,
  DeclarationProcessStepNames,
  DischargeRequestStep,
  DischargeRequestStepNames,
  RequestIncomeExpenseProcessStep,
  RequestIncomeExpenseProcessStepNames,
  StepStatus,
  type AppUser,
  type DeclarationProcessStatus,
  type LendingProduct,
  type LoanApplication,
  type RequestIncomeExpenseProcessStatus,
  type DischargeRequestProcessStatus,
} from '@/types';
import { ExclamationTriangleIcon } from '@heroicons/vue/24/solid';
import { computed, onBeforeMount, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { allowNoContractOfSale } from '@/helpers/builder';
import type { BrokerCurrentUserResponse } from '@/api/backend/brokers/user';
import type { CustomerCurrentUserResponse } from '@/api/backend/users/user';
import { mapDischargeRequest } from '@/mappers/mapFromApi';
import { useCookies } from '@vueuse/integrations/useCookies';
import axios from 'axios';
import type { RetrieveLoanApplicationResponse } from '@/types/Responses';
import ProductSelectionStep from '@/views/ProductSelectionStep.vue';
import ComplianceDisclaimerStep from '@/views/ComplianceDisclaimerStep.vue';

const props = defineProps<{
  from?: string;
  appType?: string;
  token?: string;
  broker?: string;
  brokerId?: string;
  appId: string;
  utmMetrics: UtmMetricDto;
  taskId?: string;
  referenceTaskId?: string;
  applicationDraftId?: string;
  scenarioApplicationId?: string;
}>();

const { isErhlEnabled } = useFeatureFlags();
const customerAppProcess = useLoanAppProcess();
const brokerAppProcess = useBrokerLoanAppProcess();
const erhlAppProcess = useErhlLoanAppProcess();
const declarationProcess = useDeclarationProcess();
const requestIncomeExpenseProcess = useRequestIncomeExpenseProcess();
const dischargeRequestProcess = useDischargeRequestProcess();
const cookies = useCookies();

const isReady = ref(false);
const showModal = ref(false);
const modalInfo = ref({
  title: '',
  line1: '',
  line2: '',
});

type BrokerScenarioState = typeof brokerAppProcess.state.value.context;
type CustomerScenarioState = typeof customerAppProcess.state.value.context;
type ErhlScenarioState = typeof erhlAppProcess.state.value.context;

const appKey = ref('');
const router = useRouter();
const complianceCheckConfirmation = ref(false);
/** Set once the `broker` token has been confirmed against the BE — see onBeforeMount. */
const brokerSessionVerified = ref(false);
/**
 * Whether this visitor may enter the broker journey. A `broker` token alone is not
 * enough: it can come from a `broker-token` cookie shared across *.bridgit.com.au
 * that outlived the JWT inside it.
 */
const isBrokerSession = computed(() => Boolean(props.broker) && brokerSessionVerified.value);
const scenarioState = ref<BrokerScenarioState | CustomerScenarioState | null>(null);
/** Prefilled ERHL machine context built from an ERHL scenario, consumed by handleStartErhlApplication */
const erhlScenarioState = ref<ErhlScenarioState | null>(null);
const chosenProduct = ref<LendingProduct | null>(null);
/** Whether the broker has confirmed a product choice, i.e. moved past Product Selection to the compliance step. */
const productSelectionConfirmed = ref(false);

const gotoNextStep = async (type: 'broker' | 'customer' | 'declaration' = 'declaration') => {
  if (isBrokerSession.value && type === 'broker') {
    // verify token when coming from loggedIn broker
    const { data } = await api.backend.brokers.user.currentUser();
    if (data.broker) {
      const brokerUser = data.broker;
      let brokerId = brokerUser.id;
      let brokerFullName = '';
      let brokerReferenceNumber = 'brokerReferenceNumber' in brokerUser ? brokerUser.brokerReferenceNumber ?? '' : '';
      const brokerEmail = brokerUser.email;
      const selectedBrokerId = cookies.get('broker-id');
      if (
        'brokerAssistantReferenceNumber' in brokerUser &&
        brokerUser.brokerAssistantReferenceNumber &&
        selectedBrokerId
      ) {
        const { data: brokerData } = await api.backend.brokerAssistants.broker.retrieveBroker({ id: selectedBrokerId });
        brokerId = selectedBrokerId;
        brokerFullName = [brokerData.broker.firstName, brokerData.broker.lastName].filter(Boolean).join(' ');
        brokerReferenceNumber = brokerData.broker.brokerReferenceNumber ?? '';
      }
      const currentLoanApp = brokerAppProcess.state.value.context.loanApp;
      brokerAppProcess.state.value.context.loanApp = {
        ...currentLoanApp,
        brokerId,
        brokerReferenceNumber,
        brokerFullName,
        brokerEmail,
      };
      return router.push({ name: 'broker' });
    }
  }
  const currentLoanApp = customerAppProcess.state.value.context.loanApp;
  customerAppProcess.state.value.context.loanApp = { ...currentLoanApp, utmMetrics: props.utmMetrics };
  type === 'broker'
    ? router.push({ name: 'broker' })
    : type === 'customer'
    ? router.push({ name: 'application' })
    : router.push({ name: 'declaration' });
};

const startNewApplication = () => {
  customerAppProcess.restart(false);
  gotoNextStep('customer');
};

const startExistingApplication = () => {
  const newState = JSON.parse(localStorage.getItem(appKey.value) as string);
  if (newState) {
    // A saved draft carrying a brokerId is no more proof of a live broker session than
    // the shared cookie is (JS-8865). `gotoNextStep('broker')` routes into the broker
    // journey whether or not the token verified, so an unverified visitor has to fall
    // through to the customer path rather than resume as a broker.
    if (newState.loanApp.brokerId && isBrokerSession.value) {
      cookies.set('broker-id', newState.loanApp.brokerId, { path: '/' });
      if (newState.loanApp.lendingProduct === 'erhl') {
        if (isErhlEnabled()) {
          router.push({ name: 'erhlReady' });
          erhlAppProcess.send({ type: 'RELOAD', state: newState });
        } else {
          modalInfo.value = {
            title: 'ERHL application unavailable',
            line1: 'This saved application uses a product that is not currently available on your account.',
            line2: 'if you need help accessing this application',
          };
          showModal.value = true;
        }
      } else {
        gotoNextStep('broker');
        brokerAppProcess.send({ type: 'RELOAD', state: newState });
      }
    } else if (newState.loanApp.actionType === 'updateExisting' && newState.from !== 'crm') {
      declarationProcess.send({ type: 'RELOAD', state: newState });
      router.push({ name: 'declaration' });
    } else if (newState.from === 'crm') {
      router.push({ name: 'request' });
    } else {
      if (isBrokerSession.value) {
        gotoNextStep('broker');
      } else {
        gotoNextStep('customer');
        declarationProcess.send({ type: 'RELOAD', state: newState });
      }
    }
  } else {
    appKey.value = '';
  }
};

const gotoBrokerPortal = () => {
  window.location.href = import.meta.env.VITE_TECHLEND_BROKER_URL;
};

/**
 * when `allowNoContractOfSale` is true, redirect the user to customer application page and start new application
 * @returns {void}
 */
const validateAllowContractOfSale = (): void => {
  if (allowNoContractOfSale()) {
    startNewApplication();
  }
};

/**
 * When broker prop is present, proceeds to the next step for broker application.
 * Explicitly records the chosen product on the loan app when the Product Selection
 * step is active, mirroring how the ERHL journey records its own product choice.
 * @returns {void}
 */
const handleStartBrokerApplication = async (): Promise<void> => {
  if (props.scenarioApplicationId && scenarioState.value !== null) {
    brokerAppProcess.send({ type: 'RELOAD', state: scenarioState.value as BrokerScenarioState });
  } else {
    brokerAppProcess.send({ type: 'RESTART_BROKER', brokerId: '' });
  }
  if (isErhlEnabled()) {
    brokerAppProcess.state.value.context.loanApp = {
      ...brokerAppProcess.state.value.context.loanApp,
      lendingProduct: 'bridging',
    };
  }
  gotoNextStep('broker');
};

/**
 * The product implied by a loaded scenario, or null when starting manually with no scenario
 * prefill. When a scenario predetermines the product, Product Selection is skipped entirely
 * and the broker isn't offered a choice — mirrors the prior "hide the other button" behavior.
 */
const scenarioProduct = computed<LendingProduct | null>(() => {
  if (erhlScenarioState.value !== null) return 'erhl';
  if (scenarioState.value !== null) return 'bridging';
  return null;
});

/** The product driving the current journey: a scenario's implied product, else the broker's own pick. */
const effectiveProduct = computed<LendingProduct | null>(() => scenarioProduct.value ?? chosenProduct.value);

/**
 * Proceeds to the next step for an ERHL application.
 * Reloads the ERHL scenario prefill (loan purposes, equity access method, borrower details)
 * built in onBeforeMount when converting from a scenario; otherwise starts a clean ERHL context.
 * @returns {void}
 */
const handleStartErhlApplication = async (): Promise<void> => {
  if (props.scenarioApplicationId && erhlScenarioState.value !== null) {
    erhlAppProcess.send({ type: 'RELOAD', state: erhlScenarioState.value });
  } else {
    erhlAppProcess.send({ type: 'RESTART_BROKER', brokerId: '' });
  }
  router.push({ name: 'erhlReady' });
};

/**
 * Dispatches to the correct application journey based on the effective product —
 * either the broker's Product Selection pick, or the product implied by a loaded scenario.
 * @returns {void}
 */
const handleStartApplication = (): void => {
  switch (effectiveProduct.value) {
    case 'erhl':
      handleStartErhlApplication();
      break;
    default:
      handleStartBrokerApplication();
      break;
  }
};

const appApi = useApplicationApi();
const scenarioApi = useScenarioApi();
const dischargeRequestApi = useDischargeRequestApi();

onBeforeMount(async () => {
  isReady.value = false;
  // When a broker is present, apply their auth token first, then resolve their
  // identity so the backend-resolved ERHL access is known before we decide whether
  // to surface the ERHL entry point. The token must be set before
  // resolveBrokerIdentity() so its authenticated currentUser() call succeeds —
  // otherwise a first-time/incognito broker resolves unauthenticated and wrongly
  // does not see ERHL. Brokers without access must not see/start ERHL.
  if (props.broker) {
    updateToken(props.broker);
    // The token can come from the `broker` query param or from a `broker-token`
    // cookie the broker portal wrote on the shared parent domain. That cookie has
    // no max-age while its JWT expires after `JWT_EXPIRES_IN`, so its presence is
    // not proof of a live session: a customer arriving on a referrer's link
    // (?utm_campaign=referral) in a browser that once visited the broker portal
    // would otherwise be pushed into the broker journey, where every
    // broker-guarded call fails with `invalid-jwt-token-error`. Confirm the
    // session first — a stale one is cleared and the visitor continues as a
    // customer.
    brokerSessionVerified.value = await hasLiveBrokerSession();
    if (brokerSessionVerified.value) {
      await resolveBrokerIdentity();
    }
  }
  try {
    if (['primary', 'secondary', 'broker'].includes(props.appType ?? '') && props.token) {
      updateToken(props.token);
      let result: RetrieveLoanApplicationResponse | undefined;
      if (props.appType === 'broker') {
        result = await appApi.retrieveBrokerApplication(props.appId);
      } else {
        if (props.from === 'd2c-quote' && props.scenarioApplicationId) {
          result = await scenarioApi.retrieveCustomerScenario(props.scenarioApplicationId);
        } else {
          result = await appApi.retrieveApplication(props.appId);
        }
      }

      const authorisedUser: BrokerCurrentUserResponse | CustomerCurrentUserResponse =
        props.appType === 'broker'
          ? (await api.backend.brokers.user.currentUser()).data
          : (await api.backend.users.user.currentUser()).data;

      const isBroker = 'broker' in authorisedUser;
      const isApplicant = 'applicant' in authorisedUser;

      if (result.success && (isBroker || isApplicant)) {
        const userType: AppUser =
          props.appType === 'primary' ? 'Primary' : props.appType === 'secondary' ? 'Secondary' : 'Broker';

        if (props.from === 'crm') {
          const newState = initRequestIncomeExpenseMachine({ actionType: 'updateExisting', userType });
          newState.authorisedUser = isBroker ? authorisedUser.broker : authorisedUser.applicant;
          newState.current = RequestIncomeExpenseProcessStep.Summary;
          newState.status = Object.fromEntries(
            RequestIncomeExpenseProcessStepNames.map((step) => [
              step,
              step === RequestIncomeExpenseProcessStep.Summary ? StepStatus.Ready : StepStatus.Incomplete,
            ]),
          ) as RequestIncomeExpenseProcessStatus;
          newState.loanApp = result.value || ({} as LoanApplication);
          if (
            isBroker &&
            'brokerAssistantReferenceNumber' in authorisedUser.broker &&
            authorisedUser.broker.brokerAssistantReferenceNumber &&
            newState.loanApp?.brokerId
          ) {
            const { data: brokerData } = await api.backend.brokerAssistants.broker.retrieveBroker({
              id: newState.loanApp.brokerId,
            });
            newState.loanApp.brokerFullName = [brokerData.broker.firstName, brokerData.broker.lastName]
              .filter(Boolean)
              .join(' ');
            newState.loanApp.brokerReferenceNumber = brokerData.broker.brokerReferenceNumber;
          }
          newState.loanApp.brokerReferenceNumber =
            newState?.loanApp?.brokerReferenceNumber ?? newState.authorisedUser?.brokerReferenceNumber ?? null;
          newState.loanApp.userType = userType;
          newState.from = props.from; // is this an I&E request from the CRM?
          router.push({ name: 'request' });
          requestIncomeExpenseProcess.send({ type: 'RELOAD', state: newState });
        } else if (props.from === 'discharge' || props.from === 'reviewDischarge') {
          let task = null;
          if (props?.taskId) {
            const parameters = {
              id: props.taskId,
              applicationId: props.appId,
            };
            const taskResponse =
              props.appType === 'broker'
                ? await api.backend.brokers.task.retrieveTask(parameters)
                : await api.backend.users.task.retrieveTask(parameters);
            if (taskResponse?.data?.message === 'success') {
              task = taskResponse.data.task;
              if (task?.status !== 'not_yet_started' && task?.status !== 'in_progress') {
                modalInfo.value = {
                  title: 'Discharge request already submitted',
                  line1: 'It looks like a discharge request has already been submitted',
                  line2: 'and talk to our customer support',
                };
                showModal.value = true;
                return;
              }
            } else {
              modalInfo.value = {
                title: 'Unable to retrieve discharge request details',
                line1: 'Sorry! We had trouble retrieving discharge request details',
                line2: 'and talk to our customer support',
              };
              showModal.value = true;
              return;
            }
          }

          const applicationData = result.value || ({} as LoanApplication);
          if (props.from === 'reviewDischarge' && props.referenceTaskId) {
            const parameters = {
              id: props.referenceTaskId,
              applicationId: props.appId,
            };
            const dischargeRequestTaskResponse = await api.backend.users.task.retrieveTask(parameters);
            if (dischargeRequestTaskResponse?.data?.message === 'success') {
              task = dischargeRequestTaskResponse.data.task;
              applicationData.dischargeRequest = mapDischargeRequest(applicationData, task);
            } else {
              modalInfo.value = {
                title: 'Unable to retrieve discharge request details',
                line1: 'Sorry! We had trouble retrieving discharge request details',
                line2: 'and talk to our customer support',
              };
              showModal.value = true;
              return;
            }
          }
          if (
            isBroker &&
            'brokerAssistantReferenceNumber' in authorisedUser.broker &&
            authorisedUser.broker.brokerAssistantReferenceNumber &&
            applicationData?.brokerId
          ) {
            const { data: brokerData } = await api.backend.brokerAssistants.broker.retrieveBroker({
              id: applicationData.brokerId,
            });
            applicationData.brokerFullName = [brokerData.broker.firstName, brokerData.broker.lastName]
              .filter(Boolean)
              .join(' ');
            applicationData.brokerReferenceNumber = brokerData.broker.brokerReferenceNumber;
          }
          let dischargedProperties = null;
          try {
            applicationData.userType = userType;
            dischargedProperties = await dischargeRequestApi.dischargedProperties({
              applicationId: props.appId,
              loanApplication: applicationData,
            });
          } catch (error) {
            console.error('Failed to fetch discharged properties:', error);
            modalInfo.value = {
              title: 'Unable to retrieve discharge information',
              line1: 'Sorry! We had trouble retrieving discharge information',
              line2: 'and talk to our customer support',
            };
            showModal.value = true;
            return;
          }

          // Notes:
          // `originTaskId` is the task id what we want to complete at the end
          // `task` is task data we want to display to the customer for review
          const newState = initDischargeRequestMachine({
            actionType: 'updateExisting',
            userType,
            task,
            type: props.from === 'reviewDischarge' ? 'review' : 'submit',
            originTaskId: props.taskId ?? null,
            application: applicationData,
            dischargedProperties,
          });
          newState.authorisedUser = isBroker ? authorisedUser.broker : authorisedUser.applicant;
          newState.current = DischargeRequestStep.DischargeRequest;
          newState.status = Object.fromEntries(
            DischargeRequestStepNames.map((step) => [
              step,
              step === DischargeRequestStep.DischargeRequest ? StepStatus.Ready : StepStatus.Incomplete,
            ]),
          ) as DischargeRequestProcessStatus;

          newState.loanApp = applicationData;
          newState.from = props.from;
          newState.loanApp.userType = userType;
          newState.loanApp.brokerReferenceNumber =
            newState?.loanApp?.brokerReferenceNumber ?? newState.authorisedUser?.brokerReferenceNumber ?? null;
          dischargeRequestProcess.send({ type: 'RELOAD', state: newState });
          router.push({ name: 'discharge' });
        } else if (props.from === 'd2c-quote' && props.scenarioApplicationId) {
          if (result.value) {
            customerAppProcess.send({ type: 'RESTART_CUSTOMER' });
            const newState = customerAppProcess.state.value.context;
            const newLoanApp = {
              ...result.value,
              scenarioApplicationId: props.scenarioApplicationId,
              allowNoContractOfSale: true,
            };
            newState.loanApp = newLoanApp;
            scenarioState.value = newState;
            customerAppProcess.send({ type: 'RELOAD', state: newState });
            router.push({ name: 'application' });
          }
        } else {
          const newState = initDeclarationMachine({
            actionType: 'updateExisting',
            userType,
            application: result.value || ({} as LoanApplication),
          });
          newState.authorisedUser = isBroker ? authorisedUser.broker : authorisedUser.applicant;
          newState.current = DeclarationProcessStep.Summary;
          newState.status = Object.fromEntries(
            DeclarationProcessStepNames.map((step) => [
              step,
              step === DeclarationProcessStep.Summary ? StepStatus.Ready : StepStatus.Incomplete,
            ]),
          ) as DeclarationProcessStatus;
          newState.loanApp = result.value || ({} as LoanApplication);
          newState.from = props.from;
          newState.loanApp.userType = userType;
          newState.loanApp.brokerReferenceNumber = newState.authorisedUser?.brokerReferenceNumber ?? null;
          declarationProcess.send({ type: 'RELOAD', state: newState });
          router.push({ name: 'declaration' });
        }
      } else {
        // const error = (<any>result).error;
        if (result.error === 'expired-link') {
          modalInfo.value = {
            title: 'Loan application link has expired',
            line1: 'Thank you! You have already confirmed your loan details.',
            line2: 'if you wish to speak to customer support.',
          };
        } else if (result.error === 'conversion-fail') {
          modalInfo.value = {
            title: 'Unable to retrieve loan details',
            line1: 'Sorry! We had trouble retrieving your loan details',
            line2: 'and talk to our customer support.',
          };
        } else {
          modalInfo.value = {
            title: 'Unable to retrieve loan details',
            line1: 'Sorry! There appears to be networking issues.',
            line2: 'if this problem persists.',
          };
        }
        showModal.value = true;
      }
    } else if (isBrokerSession.value) {
      // Token already applied and verified above (before resolveBrokerIdentity).
      if (props.applicationDraftId) {
        const applicationDraft = await appApi.retrieveApplicationDraft(props.applicationDraftId);
        if (
          applicationDraft.success &&
          applicationDraft?.value?.downloadUrl &&
          applicationDraft.value.downloadUrl !== 'string'
        ) {
          const jsonFile = await axios.get(applicationDraft.value.downloadUrl);
          const newState = jsonFile.data.state;
          const { data } = await api.backend.brokers.user.currentUser();
          const brokerUser = data.broker;

          let brokerId = brokerUser.id;
          let brokerFullName = '';
          let brokerReferenceNumber =
            'brokerReferenceNumber' in brokerUser ? brokerUser.brokerReferenceNumber ?? '' : '';
          let brokerEmail = brokerUser.email;
          const selectedBrokerId = applicationDraft.value?.brokerId || cookies.get('broker-id');
          if (selectedBrokerId) {
            cookies.set('broker-id', selectedBrokerId, { path: '/' });
          }
          if (
            'brokerAssistantReferenceNumber' in brokerUser &&
            brokerUser.brokerAssistantReferenceNumber &&
            selectedBrokerId
          ) {
            const { data: brokerData } = await api.backend.brokerAssistants.broker.retrieveBroker({
              id: selectedBrokerId,
            });
            brokerId = selectedBrokerId;
            brokerFullName = [brokerData.broker.firstName, brokerData.broker.lastName].filter(Boolean).join(' ');
            brokerEmail = brokerData.broker.email ?? '';
            brokerReferenceNumber = brokerData.broker.brokerReferenceNumber ?? '';
          }
          const newLoanApp = {
            ...newState.loanApp,
            brokerId,
            brokerReferenceNumber,
            brokerFullName,
            brokerEmail,
            applicationDraftId: props.applicationDraftId,
          };
          newState.loanApp = newLoanApp;

          // ERHL drafts must resume into the ERHL wizard/state machine, not the bridging
          // broker flow — the two machines expect differently-shaped step state. When ERHL
          // access is disabled, surface an unavailable message instead of feeding this
          // erhl-shaped state into the broker machine.
          if (newState.loanApp.lendingProduct === 'erhl') {
            if (isErhlEnabled()) {
              erhlAppProcess.send({ type: 'RESTART_BROKER', brokerId: brokerId ?? '' });
              router.push({ name: 'erhlReady' });
              erhlAppProcess.send({ type: 'RELOAD', state: newState });
            } else {
              modalInfo.value = {
                title: 'ERHL application unavailable',
                line1: 'This saved application uses a product that is not currently available on your account.',
                line2: 'if you need help accessing this application',
              };
              showModal.value = true;
            }
          } else {
            brokerAppProcess.send({ type: 'RESTART_BROKER', brokerId: '' });
            router.push({ name: 'broker' });
            brokerAppProcess.send({ type: 'RELOAD', state: newState });
          }
          return;
        }
      }
      if (props.scenarioApplicationId) {
        const applicationScenario = await scenarioApi.retrieveBrokerScenario(props.scenarioApplicationId);
        if (applicationScenario.value) {
          const isErhlScenario = applicationScenario.value.lendingProduct === 'erhl';
          // An ERHL scenario carries erhl-shaped data the bridging machine cannot represent, and only
          // brokers with access (erhl-product flag AND resolved ERHL access) may enter the wizard. Bail out before
          // arming any journey state — like the draft-resume paths, which never reach the compliance step:
          // leaving compliance armed behind the modal lets Start run with no scenario refs loaded, which
          // opens an empty bridging application and drops the prefill.
          if (isErhlScenario && !isErhlEnabled()) {
            modalInfo.value = {
              title: 'ERHL application unavailable',
              line1: 'This scenario uses a product that is not currently available on your account.',
              line2: 'if you need help converting this scenario',
            };
            showModal.value = true;
            return;
          }
          const { data } = await api.backend.brokers.user.currentUser();
          const brokerUser = data.broker;
          let brokerId = brokerUser.id;
          let brokerFullName = '';
          let brokerReferenceNumber =
            'brokerReferenceNumber' in brokerUser ? brokerUser.brokerReferenceNumber ?? '' : '';
          let brokerEmail = brokerUser.email;
          const selectedBrokerId = cookies.get('broker-id');

          if (
            'brokerAssistantReferenceNumber' in brokerUser &&
            brokerUser.brokerAssistantReferenceNumber &&
            selectedBrokerId
          ) {
            const { data: brokerData } = await api.backend.brokerAssistants.broker.retrieveBroker({
              id: selectedBrokerId,
            });
            brokerId = selectedBrokerId;
            brokerFullName = [brokerData.broker.firstName, brokerData.broker.lastName].filter(Boolean).join(' ');
            brokerEmail = brokerData.broker.email ?? '';
            brokerReferenceNumber = brokerData.broker.brokerReferenceNumber ?? '';
          }
          complianceCheckConfirmation.value = true;
          const newLoanApp = {
            ...applicationScenario.value,
            brokerId,
            brokerReferenceNumber,
            brokerFullName,
            brokerEmail,
            scenarioApplicationId: props.scenarioApplicationId,
          };

          // ERHL scenarios prefill the dedicated ERHL wizard; bridging scenarios use the standard broker flow.
          if (isErhlScenario) {
            erhlAppProcess.send({ type: 'RESTART_BROKER', brokerId: brokerId ?? '' });
            const newState = erhlAppProcess.state.value.context;
            newState.loanApp = newLoanApp;
            // ERHL is identified by lendingProduct; the wizard keys its flow (incl. the Loan Purpose step)
            // off loanType === EQUITY_RELEASE_HOME_LOAN, so normalise it here to match a manual ERHL start.
            // The scenario carries the base loanType (e.g. buy_now); without this the Loan Purpose step is skipped.
            newState.loanApp.loanType = LoanType.EQUITY_RELEASE_HOME_LOAN;
            newState.loanApp.complianceCheck = false;
            erhlScenarioState.value = newState;
            return;
          }

          brokerAppProcess.send({ type: 'RESTART_BROKER', brokerId: brokerId ?? '' });
          const newState = brokerAppProcess.state.value.context;
          newState.loanApp = newLoanApp;
          newState.loanApp.complianceCheck = false;
          scenarioState.value = newState;
          return;
        }
      }

      if (props.brokerId) {
        cookies.set('broker-id', props.brokerId, { path: '/' });
        complianceCheckConfirmation.value = true;
      }
      const referrer = window.document.referrer;
      const currentUrl = window.location.href;
      if (referrer && !referrer.includes(currentUrl)) {
        if (!localStorage.getItem(CurrentKey)) {
          brokerAppProcess.send({ type: 'RESTART_BROKER', brokerId: props.brokerId ?? '' });
        }

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        delete window.document.referrer;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window.document as any).__defineGetter__('referrer', function () {
          return currentUrl;
        });
      }
    }
  } finally {
    isReady.value = true;
    validateAllowContractOfSale();
  }
});

const trustpilot = ref();
onMounted(() => {
  if (window.Trustpilot) {
    window.Trustpilot.loadFromElement(trustpilot.value, true);
  }
  appKey.value = localStorage.getItem(CurrentKey) ?? '';
});
</script>

<script lang="ts">
declare global {
  interface Window {
    // eslint-disable-next-line
    Trustpilot: any;
  }
}
</script>

<template>
  <div class="flex w-full flex-col items-center">
    <div v-if="!isReady" class="mt-[20%] text-center">
      <BxSpinner size="4rem" />
      <div>Loading application</div>
    </div>
    <div v-else class="mt-16 grid w-full place-items-center">
      <div
        class="invisible absolute flex w-full flex-col rounded border border-gray-100 bg-white p-8 opacity-0 shadow-lg transition-opacity duration-500 sm:w-3/4 lg:w-1/2"
        :class="[
          appKey && '!visible !static !opacity-100',
          (isBrokerSession || props.scenarioApplicationId) && complianceCheckConfirmation && 'lg:!w-2/3',
          (isBrokerSession || props.scenarioApplicationId) && complianceCheckConfirmation && 'min-h-[575px]',
        ]"
      >
        <div
          class="flex flex-col items-center text-center"
          :class="isBrokerSession && complianceCheckConfirmation && 'flex-1'"
        >
          <h1 class="w-full text-center text-2xl font-medium">
            Welcome to the {{ AppValues.COMPANY_NAME }} Application
          </h1>
          <template v-if="(isBrokerSession || props.scenarioApplicationId) && complianceCheckConfirmation">
            <!--
              Product Selection and the compliance disclaimer are two separate steps/components.
              Product Selection only shows for brokers with ERHL enabled and no product chosen yet;
              otherwise the compliance disclaimer shows (its own "Start Application" button is only
              rendered for brokers, matching the pre-existing gating on this whole action).
            -->
            <div class="grid w-full flex-1">
              <ProductSelectionStep
                v-if="isBrokerSession && isErhlEnabled() && !scenarioProduct"
                class="col-start-1 row-start-1 w-full"
                :class="productSelectionConfirmed && 'invisible'"
                :model-value="chosenProduct"
                @select="
                  (product) => {
                    chosenProduct = product;
                    productSelectionConfirmed = true;
                  }
                "
              />
              <ComplianceDisclaimerStep
                :key="productSelectionConfirmed ? 'confirmed' : 'pending'"
                class="col-start-1 row-start-1 w-full"
                :class="isBrokerSession && isErhlEnabled() && !scenarioProduct && !productSelectionConfirmed && 'invisible'"
                :product="isBrokerSession && isErhlEnabled() ? effectiveProduct : null"
                :show-start-button="isBrokerSession"
                :show-back-button="!!(isBrokerSession && isErhlEnabled() && !scenarioProduct)"
                @confirm="handleStartApplication"
                @back="productSelectionConfirmed = false"
              />
            </div>
          </template>
          <div v-else class="w-full">
            <div class="mb-4 mt-8 flex w-full flex-col items-center rounded-lg border border-gray-300 p-4 text-center">
              <ExclamationTriangleIcon class="h-10 w-10 text-amber-400" />
              <span class="mt-2 text-lg font-medium">We found an application still in progress</span>
            </div>
            <span>Would you like to continue the current application <br />or start a new one?</span>
          </div>
        </div>

        <div v-if="!(isBrokerSession && complianceCheckConfirmation)" class="mt-6 flex flex-wrap gap-6">
          <BxButton class="bullet-item justify-center" variant="primary" @click="startExistingApplication()">
            Continue
          </BxButton>
          <BxButton
            class="bullet-item justify-center"
            variant="primary"
            @click="
              () => {
                if (isBrokerSession && !complianceCheckConfirmation) {
                  complianceCheckConfirmation = true;
                  chosenProduct = null;
                } else {
                  appKey = '';
                }
              }
            "
            >Start new</BxButton
          >
        </div>
      </div>
      <div
        class="invisible absolute w-full rounded border border-gray-100 bg-white p-8 opacity-0 shadow-lg transition-opacity duration-500 sm:w-3/4 lg:w-1/2"
        :class="!appKey && '!visible !static !opacity-100'"
      >
        <h1 class="w-full text-center text-2xl font-medium">Welcome to the {{ AppValues.COMPANY_NAME }} Application</h1>
        <div class="mt-8 flex flex-wrap gap-6">
          <div class="bullet-item">
            <div>
              <BxIcon name="stopwatch" class="h-8 w-8" />
            </div>
            <span>5 to 10 minutes to apply</span>
          </div>
          <div class="bullet-item">
            <div>
              <BxIcon name="docSearch" class="h-8 w-8" />
            </div>
            <span>No credit check until you qualify</span>
          </div>
          <div class="bullet-item">
            <div>
              <BxIcon name="padlock" class="h-8 w-8" />
            </div>
            <span>Information is protected by bank&#8209;level security</span>
          </div>
          <div class="bullet-item">
            <div>
              <BxIcon name="circleTick" class="h-8 w-8" />
            </div>
            <span>No obligation to proceed after applying</span>
          </div>
        </div>
        <div class="mt-8 flex flex-wrap gap-6">
          <BxButton
            id="customer-type-customer"
            class="bullet-item justify-center"
            variant="primary"
            @click="startNewApplication"
          >
            I'm a Customer
          </BxButton>
          <BxButton
            id="customer-type-broker"
            class="bullet-item justify-center"
            variant="primary"
            @click="gotoBrokerPortal()"
          >
            I'm a Broker
          </BxButton>
        </div>
        <div class="mt-4">
          <!-- TrustBox widget - Micro TrustScore -->
          <div
            ref="trustpilot"
            class="trustpilot-widget"
            data-locale="en-US"
            data-template-id="5419b637fa0340045cd0c936"
            data-businessunit-id="6216ad9ba106dfb98a8b5eaf"
            data-style-height="20px"
            data-style-width="100%"
            data-theme="light"
          >
            <a href="https://www.trustpilot.com/review/bridgit.com.au" target="_blank" rel="noopener">Trustpilot</a>
          </div>
          <!-- End TrustBox widget -->
        </div>
      </div>
    </div>
    <BxModal
      :open="showModal"
      :icon="ExclamationTriangleIcon"
      :title="modalInfo.title"
      cancel-label="Cancel"
      confirm-label="Confirm"
    >
      <div class="mt-6 leading-loose">
        <p>{{ modalInfo.line1 }}</p>
        <p>
          Please contact {{ AppValues.COMPANY_NAME }} on
          <a class="link" :href="`tel:${AppValues.CONTACT}`">{{ AppValues.CONTACT }}</a>
          {{ modalInfo.line2 }}.
        </p>
      </div>
      <template #buttons>
        <BxButton variant="primary" @click="showModal = false">Ok</BxButton>
      </template>
    </BxModal>
  </div>
</template>

<style lang="scss" scoped>
.bullet-item {
  display: flex;
  flex-basis: 100%;
  gap: 0.75rem;
  align-items: center;

  @media (min-width: 640px) {
    flex-basis: calc(50% - 0.75rem);
  }
}

.link {
  @apply cursor-pointer font-medium text-bridgit-navy underline hover:text-bridgit-vibrantGreen;
}
</style>
